import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
// eslint-disable-next-line import/named
import { TranslateFunction } from 'react-localize-redux';

import { TextField } from '../../../../components/atoms';
import { Location as OrderAreaIcon } from '../../../../components/icons';
import { SectionGroup } from '../../../../components/molecules';
import * as Api from '../../../../weborder/Api';
import { getLocationDetails } from '../../../RestaurantMenu/utils/locationHeaderUtils';
import { checkoutUtils } from '../../utils';
import {
  CheckoutType,
  ParamProps,
  ServiceTypes,
} from '../../utils/checkoutUtils';
import { OrderTypeOptions, SelectTimeField } from './partials';

type Props = {
  configData: Api.AppConfig;
  deliveryLocation: Api.DeliveryLocation;
  disabled: boolean;
  handleChangeDeliveryTime: (time: string) => void;
  handleNumber: (e: any) => any;
  hasRequestFullNameFeatureFlag: boolean;
  loading: boolean;
  params: ParamProps;
  pickupLocationType: Api.PickupLocationType;
  requestedTimeOrDefault: string;
  restaurant: Api.CollectionRestaurantDetail | Api.DeliveryRestaurantDetail;
  serviceTypes: ServiceTypes;
  setUserName: (data: { name: string }) => void;
  tableNumber: number;
  toggleTableDialog: () => void;
  translate: TranslateFunction;
  type: CheckoutType;
  userName: string;
};

const useStyles = makeStyles((theme: Theme) => ({
  displayAddress: {
    alignItems: 'flex-start',
    display: 'flex',
    flexFlow: 'column nowrap',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },

  locationSection: {
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      marginBottom: 'inherit',
    },

    '& .fd-icon__ordercollection': {
      marginBottom: theme.spacing(0.6),
    },
  },

  locationIcon: {
    alignItems: 'center',
    display: 'inline-flex',
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(1),

    '& .fd-icon': {
      paddingRight: theme.spacing(1.2),
      display: 'none',

      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
    },

    '& .fd-text:first-of-type': {
      paddingRight: theme.spacing(0.5),
    },
  },
}));

export function ServiceSection(props: Props): JSX.Element {
  const {
    configData,
    deliveryLocation,
    disabled,
    handleChangeDeliveryTime,
    handleNumber,
    hasRequestFullNameFeatureFlag,
    loading,
    params,
    pickupLocationType,
    requestedTimeOrDefault,
    restaurant,
    serviceTypes,
    setUserName,
    tableNumber,
    toggleTableDialog,
    translate,
    type,
    userName,
  } = props;

  const classes = useStyles();

  const {
    isDineInAndTakeOut,
    isDriveThrough,
    isTableServiceAndTakeOut,
    isTableServiceOnly,
  } = serviceTypes;

  const locationDetails = getLocationDetails(
    restaurant,
    deliveryLocation,
    classes,
    'body1',
    translate,
  );

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserName({
      name: event.currentTarget.value,
    });
  };

  const nameTitle = checkoutUtils.getNameTitle(
    hasRequestFullNameFeatureFlag,
    type,
    isTableServiceOnly,
    translate,
  );

  const tableServiceCategory =
    checkoutUtils.getTableServiceCategory(restaurant);

  const pickupLocationOptions =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.PickupLocationOptions
      ? restaurant.PickupLocationOptions
      : undefined;

  const locationAreas =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.LocationAreas
      ? restaurant.LocationAreas
      : undefined;

  const hasExtraServiceOptions =
    isDineInAndTakeOut ||
    isDriveThrough ||
    isTableServiceAndTakeOut ||
    isTableServiceOnly;

  const isPreorderAvailable =
    restaurant.AcceptPreOrders || !!restaurant.PreOrderOptions;

  return (
    <SectionGroup
      className={classes.locationSection}
      icon={locationDetails.icon}
      sectionName="service"
      title={locationDetails.title}
    >
      <div className={classes.locationIcon}>
        {!serviceTypes.isDelivery && <OrderAreaIcon size="xs" />}
        <div className={classes.displayAddress}>{locationDetails.address}</div>
      </div>

      {hasExtraServiceOptions && (
        <OrderTypeOptions
          handleNumber={handleNumber}
          pickupLocationOptions={pickupLocationOptions}
          locationAreas={locationAreas}
          serviceTypes={serviceTypes}
          tableNumber={tableNumber}
          tableServiceCategory={tableServiceCategory}
          toggleTableDialog={toggleTableDialog}
          vrId={params.vrid}
        />
      )}

      {isPreorderAvailable && (
        <SelectTimeField
          changeDeliveryTime={handleChangeDeliveryTime}
          configData={configData}
          disabled={disabled || loading}
          pickupLocationType={pickupLocationType}
          requestedDeliveryOrPickupTimeUtc={requestedTimeOrDefault}
          restaurant={restaurant}
          serviceTypes={serviceTypes}
          translate={translate}
        />
      )}

      <TextField
        fullWidth
        id="customerName"
        label={nameTitle}
        onChange={handleNameChange}
        value={userName}
      />
    </SectionGroup>
  );
}
